import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { Activity, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';

const MarketData = () => {
  const { get, loading } = useApi();
  const [stocks, setStocks] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const data = await get('/ml/market-data');
      if (data.stocks) {
        setStocks(data.stocks);
        setLastUpdated(new Date().toLocaleTimeString('en-IN'));
      }
    } catch { /* handled */ }
  }, [get]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Activity className="h-8 w-8 text-red-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Market Data</h1>
            <p className="text-gray-500">Live Nifty 50 stock prices</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && <span className="text-xs text-gray-400">Updated: {lastUpdated}</span>}
          <button onClick={loadData} className="btn-secondary flex items-center gap-2 text-sm" disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {stocks.length > 0 ? (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-3 font-medium text-gray-600">Stock</th>
                <th className="text-right py-3 px-3 font-medium text-gray-600">Price (₹)</th>
                <th className="text-right py-3 px-3 font-medium text-gray-600">Change</th>
                <th className="text-right py-3 px-3 font-medium text-gray-600">Change %</th>
                <th className="text-right py-3 px-3 font-medium text-gray-600">High</th>
                <th className="text-right py-3 px-3 font-medium text-gray-600">Low</th>
                <th className="text-right py-3 px-3 font-medium text-gray-600">Volume</th>
              </tr>
            </thead>
            <tbody>
              {stocks.map((stock, idx) => {
                const isUp = stock.change >= 0;
                return (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-3">
                      <span className="font-semibold text-gray-800">{stock.ticker?.replace('.NS', '')}</span>
                    </td>
                    <td className="py-3 px-3 text-right font-semibold text-gray-800">
                      ₹{stock.price?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </td>
                    <td className={`py-3 px-3 text-right font-medium flex items-center justify-end gap-1 ${isUp ? 'text-green-600' : 'text-red-600'}`}>
                      {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {stock.change >= 0 ? '+' : ''}{stock.change?.toFixed(2)}
                    </td>
                    <td className={`py-3 px-3 text-right font-medium ${isUp ? 'text-green-600' : 'text-red-600'}`}>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${isUp ? 'bg-green-50' : 'bg-red-50'}`}>
                        {stock.changePct >= 0 ? '+' : ''}{stock.changePct?.toFixed(2)}%
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right text-gray-600">
                      ₹{stock.high?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-3 text-right text-gray-600">
                      ₹{stock.low?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-3 text-right text-gray-500">
                      {stock.volume ? (stock.volume / 1000000).toFixed(2) + 'M' : 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : loading ? (
        <div className="card text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading market data...</p>
        </div>
      ) : (
        <div className="card text-center py-12">
          <Activity className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700">Unable to load market data</h3>
          <p className="text-gray-500 mt-2">Market data service is temporarily unavailable. Please try again later.</p>
        </div>
      )}
    </div>
  );
};

export default MarketData;
